import React from 'react';
import styled from '@emotion/styled';
import { DIVIDER_COLORS, TEXT_COLORS } from 'Style/colors';
import { SPACING } from 'Style/spacing';
import { CONTENT_STORY_HEADING_TYPES, BODY_TYPES } from 'Style/typography';

import { NativeAdFields } from './native-ad-render';

// Promoted-post tile matching a real story tile (StyledListItem in
// Webapp/shared/app/components/item.tsx: 2px border, MEDIUM radius, clipped).
// The WHOLE card is the click-through anchor to the native ad's click URL so
// GAM click tracking fires on the natural navigation.
const StyledNativeAdLink = styled.a({
  display: 'flex',
  flexDirection: 'column',
  border: `2px solid ${DIVIDER_COLORS.primary}`,
  borderRadius: SPACING.MEDIUM,
  overflow: 'hidden',
  height: '100%',
  textDecoration: 'none',
  color: 'inherit',
});

// Story-tile image: full-bleed at the top, 16:9 like the card media.
const StyledNativeAdImage = styled.div<{ src: string }>((props) => ({
  width: '100%',
  aspectRatio: '16 / 9',
  backgroundImage: `url("${props.src}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: 'var(--color--surface-secondary)',
}));

const StyledNativeAdBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: SPACING.BASE,
  padding: SPACING.LARGE,
});

const StyledNativeAdHeadline = styled.div(CONTENT_STORY_HEADING_TYPES.SMALL);

const StyledNativeAdAdvertiser = styled.div(BODY_TYPES.SMALL_STANDARD, {
  color: TEXT_COLORS.tertiary,
});

/**
 * Render a self-rendered GAM native creative's fields into a Flipboard
 * promoted-post card. Passed to AdSlot as its `renderNative` render-prop; only
 * ever called with valid, non-empty fields (native-ad-render.ts drops
 * blank/no-fill payloads, so headline OR image is always present and clickUrl
 * is always set).
 */
export const renderNativeAdCard = (fields: NativeAdFields): React.ReactNode => (
  <StyledNativeAdLink
    className="infeed-ad-card__native"
    href={fields.clickUrl}
    // The click URL carries GAM click macros; open in a new tab like a story.
    target="_blank"
    rel="noopener noreferrer sponsored"
  >
    {fields.mainImage && (
      <StyledNativeAdImage
        className="infeed-ad-card__native-image"
        src={fields.mainImage}
        role="img"
        // Always a non-empty accessible name for the role="img" element.
        aria-label={fields.headline || fields.advertiser || 'Advertisement'}
      />
    )}
    <StyledNativeAdBody>
      {fields.headline && (
        <StyledNativeAdHeadline className="infeed-ad-card__native-headline">
          {fields.headline}
        </StyledNativeAdHeadline>
      )}
      {fields.advertiser && (
        <StyledNativeAdAdvertiser className="infeed-ad-card__native-advertiser">
          {fields.advertiser}
        </StyledNativeAdAdvertiser>
      )}
    </StyledNativeAdBody>
  </StyledNativeAdLink>
);
